/** Fixed intro step: coordinator + entry category (not assignable in builder). */
export const SETUP_PAGE_ID = '__setup__';
export const ENTRY_DETAILS_PAGE_KEY = 'page_entry_details';

export function isEntryDetailsPage(page) {
  if (!page) return false;
  const k = String(page.key || page.id || '').trim().toLowerCase();
  const t = String(page.title || '').trim().toLowerCase();
  return k === 'page_entry_details' || t === 'entry details';
}

export function findEntryDetailsPage(pages = []) {
  return (pages || []).find(isEntryDetailsPage) || null;
}

export function resolvePublicFormPages(settings = {}) {
  const cfg = settings?.publicFormPages || {};
  const enabled = Boolean(cfg.enabled);
  const pages = (Array.isArray(cfg.pages) ? cfg.pages : [])
    .map((p, idx) => ({
      key: String(p?.key || `page_${idx + 1}`).trim(),
      title: String(p?.title || `Page ${idx + 1}`).trim(),
      description: String(p?.description || '').trim(),
      order: Number.isFinite(Number(p?.order)) ? Number(p.order) : idx,
    }))
    .filter((p) => p.key)
    .sort((a, b) => a.order - b.order);
  return { enabled: enabled && pages.length > 0, pages };
}

export function isPublicFormPaginationEnabled(settings = {}) {
  return resolvePublicFormPages(settings).enabled;
}

export function defaultPublicFormPageKey(pages = []) {
  return pages[0]?.key || 'page_1';
}

export function resolveSectionPageKey(section, pages, paginationEnabled) {
  if (!paginationEnabled) return null;
  const pk = String(section?.pageKey || '').trim();
  if (pk && pages.some((p) => p.key === pk)) return pk;
  return defaultPublicFormPageKey(pages);
}

/** Wizard steps shown on public / portal / edit / view forms. */
export function buildPublicFormSteps(settings = {}) {
  const { enabled, pages } = resolvePublicFormPages(settings);
  if (!enabled) return null;
  const coordinatorTitle = String(settings?.coordinator?.labels?.sectionHeading || '').trim();
  const hasEntryDetailsPage = pages.some(isEntryDetailsPage);
  const setupTitle = coordinatorTitle || 'Coordinator Details';
  const setupDesc = hasEntryDetailsPage
    ? 'Coordinator contact details'
    : 'Coordinator and entry category';
  return [
    {
      id: SETUP_PAGE_ID,
      title: setupTitle,
      description: setupDesc,
      fixed: true,
    },
    ...pages.map((p) => ({
      id: p.key,
      title: p.title || 'Questions',
      description: p.description || '',
      fixed: false,
    })),
  ];
}

export function sectionsForPublicFormStep(orderedSections, stepId, settings) {
  const { enabled, pages } = resolvePublicFormPages(settings);
  if (!enabled || !stepId || stepId === SETUP_PAGE_ID) return [];
  return (orderedSections || []).filter(
    (s) => resolveSectionPageKey(s, pages, true) === stepId
  );
}

export function createDefaultPublicFormPages(count = 2) {
  return Array.from({ length: Math.max(1, count) }, (_, i) => ({
    key: `page_${i + 1}`,
    title: i === 0 ? 'Page 1' : `Page ${i + 1}`,
    description: '',
    order: i,
  }));
}

export function normalizePublicFormPagesForSave(pages = []) {
  return (Array.isArray(pages) ? pages : [])
    .map((p, idx) => ({
      key: String(p?.key || `page_${idx + 1}`).trim(),
      title: String(p?.title || `Page ${idx + 1}`).trim(),
      description: String(p?.description || '').trim(),
      order: idx,
    }))
    .filter((p) => p.key);
}

/** Assign sections to pages when pagination is first enabled (round-robin across pages). */
export function assignDefaultSectionPageKeys(sections = [], pages = []) {
  if (!pages.length) return sections;
  let assignIdx = 0;
  return sections.map((s) => {
    if (String(s?.pageKey || '').trim()) return s;
    const pageKey = pages[assignIdx % pages.length]?.key || defaultPublicFormPageKey(pages);
    assignIdx += 1;
    return { ...s, pageKey };
  });
}

/**
 * One portal page per PDF pack so Separate PDF sections can sit on their own wizard step.
 * Page keys are `pdf_<packKey>` so they stay stable when pack titles change.
 */
export function createPublicFormPagesFromPdfPacks(pdfOutputs = []) {
  const packs = (Array.isArray(pdfOutputs) ? pdfOutputs : [])
    .map((p, idx) => ({
      key: String(p?.key || '').trim(),
      title: String(p?.title || '').trim() || String(p?.key || `Pack ${idx + 1}`).trim(),
      order: Number.isFinite(Number(p?.order)) ? Number(p.order) : idx,
    }))
    .filter((p) => p.key)
    .sort((a, b) => a.order - b.order);
  if (!packs.length) return createDefaultPublicFormPages(2);
  return packs.map((p, idx) => ({
    key: `pdf_${p.key}`,
    title: p.title,
    description: idx === 0
      ? 'Application questions for the main PDF'
      : `Questions for the separate PDF: ${p.title}`,
    order: idx,
  }));
}

/** Map each section onto the portal page that matches its PDF pack (forces reassignment). */
export function assignSectionPageKeysByPdfPack(sections = [], pages = [], pdfOutputs = []) {
  if (!pages.length) return sections;
  const packKeys = new Set(
    (Array.isArray(pdfOutputs) ? pdfOutputs : []).map((p) => String(p?.key || '').trim()).filter(Boolean)
  );
  const pageByPack = new Map();
  pages.forEach((page) => {
    const pk = String(page?.key || '').trim();
    if (!pk) return;
    if (pk.startsWith('pdf_')) {
      const packKey = pk.slice(4);
      if (packKey) pageByPack.set(packKey, pk);
    }
  });
  const fallback = defaultPublicFormPageKey(pages);
  return sections.map((s) => {
    const packKey = String(s?.pdfOutputKey || 'base').trim() || 'base';
    const resolvedPack = packKeys.has(packKey) ? packKey : 'base';
    const pageKey = pageByPack.get(resolvedPack)
      || pages.find((p) => p.key === `pdf_${resolvedPack}`)?.key
      || fallback;
    return { ...s, pageKey };
  });
}

/** True when both features are on — they are orthogonal; this only flags combined use. */
export function publicFormPagesAndPdfPacksBothEnabled(settings = {}) {
  const pagesOn = isPublicFormPaginationEnabled(settings);
  const packs = Array.isArray(settings?.pdfOutputs) ? settings.pdfOutputs : [];
  return pagesOn && packs.length > 1;
}
